import { useState, useRef, useCallback, useEffect } from 'react';
import { Plus, BookOpen, MoreVertical, Sparkles, Moon, Sun, Settings, Upload, FolderOpen, Coffee, User as UserIcon, LogOut, PenLine, Clock, ArrowRight, Inbox, MessageSquareQuote, X as XIcon, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAppStore } from '@/lib/store';
import type { Novel } from '@/lib/store';
import { useMobile } from '@/hooks/use-mobile';
import { exportToDocx } from '@/lib/export';
import { DailyPromptCard } from './DailyPrompt';
import { downloadBackup } from '@/lib/json-backup';
import CreateNovelForm from './CreateNovelForm';
interface DashboardProps {
  onSelectNovel: () => void;
}

// Book spine colors - warm, literary tones with cloth texture
const bookSpineColors = [
  { bg: '#8B4513', text: '#F5E6D3', accent: '#D4A574', texture: 'cloth' }, // Saddle Brown
  { bg: '#2F4F4F', text: '#E8E8E8', accent: '#5F9EA0', texture: 'leather' }, // Dark Slate
  { bg: '#4A3728', text: '#F5E6D3', accent: '#C4A77D', texture: 'cloth' }, // Coffee
  { bg: '#1C3A3A', text: '#E8F0E8', accent: '#6B8E7A', texture: 'leather' }, // Deep Forest
  { bg: '#3D3D3D', text: '#E8E8E8', accent: '#A0A0A0', texture: 'cloth' }, // Charcoal
  { bg: '#5D4E37', text: '#F5E6D3', accent: '#B8A080', texture: 'leather' }, // Taupe
  { bg: '#2C3E50', text: '#ECF0F1', accent: '#5DADE2', texture: 'cloth' }, // Midnight Blue
  { bg: '#4A3C31', text: '#F5E6D3', accent: '#A08060', texture: 'leather' }, // Dark Wood
];

// Cloth texture SVG pattern (data URI)
const clothTexture = `url("data:image/svg+xml,%3Csvg viewBox='0 0 4 4' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.08'/%3E%3C/svg%3E")`;

// Leather texture with subtle grain
const leatherTexture = `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='grain'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.04' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23grain)' opacity='0.06'/%3E%3C/svg%3E")`;

const generateBookPattern = (index: number, textureType: 'cloth' | 'leather' = 'cloth') => {
  const texture = textureType === 'cloth' ? clothTexture : leatherTexture;
  const patterns = [
    `${texture}, linear-gradient(135deg, transparent 25%, rgba(255,255,255,0.08) 25%, rgba(255,255,255,0.08) 50%, transparent 50%)`,
    `${texture}, linear-gradient(to bottom, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(0,0,0,0.08) 100%)`,
    `${texture}, repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.04) 2px, rgba(255,255,255,0.04) 4px)`,
    `${texture}, radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.08) 0%, transparent 50%)`,
  ];
  return patterns[index % patterns.length];
};

// Calculate spine width based on word count (thicker books for more content)
const getSpineWidth = (wordCount: number, isMobile: boolean) => {
  const baseWidth = isMobile ? 38 : 48;
  const extraWidth = Math.min(24, Math.floor(wordCount / 8000)); // Add up to 24px for long books
  return baseWidth + extraWidth;
};

// Parse and validate a .nexus file
async function parseNexusFile(file: File): Promise<Novel | null> {
  try {
    const text = await file.text();
    const data = JSON.parse(text) as Novel;
    // Basic shape validation
    if (!data.id || !data.title || !Array.isArray(data.chapters)) {
      throw new Error('Invalid .nexus file format');
    }
    return data;
  } catch {
    return null;
  }
}

export function Dashboard({ onSelectNovel }: DashboardProps) {
  const {
    novels,
    activeNovelId,
    setActiveNovel,
    deleteNovel,
    importNovel,
    theme,
    setTheme,
    setShowSettings,
    themeProfile,
    setViewMode,
  } = useAppStore();

  const { user, logout } = useAuth();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0); // track nested drag enter/leave
  const { isMobile } = useMobile();

  // --- Warm Start Detection ---
  const [showWarmStart, setShowWarmStart] = useState(() => {
    try {
      const lastSession = localStorage.getItem('nexus_last_session_ts');
      const now = Date.now();
      if (lastSession) {
        const elapsed = now - parseInt(lastSession, 10);
        // Show warm start if returning after more than 1 hour
        return elapsed > 60 * 60 * 1000;
      }
      return false;
    } catch {
      return false;
    }
  });

  // Update last session timestamp on mount
  useEffect(() => {
    try {
      localStorage.setItem('nexus_last_session_ts', Date.now().toString());
    } catch { /* noop */ }
  }, []);

  // --- Author Inbox ---
  const [showInbox, setShowInbox] = useState(false);
  const [inboxTips, setInboxTips] = useState<Array<{
    id: string;
    readerId: string;
    workId: string;
    highlightedText: string;
    note: string;
    createdAt: string;
  }>>([]);
  const [inboxLoading, setInboxLoading] = useState(false);

  // Load inbox when opened
  useEffect(() => {
    if (!showInbox || !user) return;
    setInboxLoading(true);
    // Fetch Cappuccino tips from the Cloudflare Worker
    import('@/contexts/AuthContext').then(({ API_URL }) => {
      fetch(`${API_URL}/api/authors/${user.sub}/marginalia`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : [])
        .then((data: any[]) => {
          setInboxTips(Array.isArray(data) ? data : []);
        })
        .catch(() => setInboxTips([]))
        .finally(() => setInboxLoading(false));
    });
  }, [showInbox, user]);

  const handleSelectNovel = (novelId: string) => {
    setActiveNovel(novelId);
    onSelectNovel();
  };

  const handleExportDocx = async (novelId: string) => {
    const novel = novels.find(n => n.id === novelId);
    if (novel) await exportToDocx(novel, "Author Name");
    setMenuOpen(null);
  };

  const handleDownloadBackup = (novelId: string) => {
    const novel = novels.find(n => n.id === novelId);
    if (novel) downloadBackup(novel);
    setMenuOpen(null);
  };

  const handleDeleteNovel = (novelId: string) => {
    deleteNovel(novelId);
    setMenuOpen(null);
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  // -----------------------------------------------------------------------
  // Import logic — shared between button click and drag-drop
  // -----------------------------------------------------------------------
  const handleImportFile = useCallback(async (file: File) => {
    setImportError(null);
    setImportSuccess(null);

    if (!file.name.endsWith('.nexus') && !file.name.endsWith('.json')) {
      setImportError('Please drop a .nexus project file.');
      setTimeout(() => setImportError(null), 4000);
      return;
    }

    const novel = await parseNexusFile(file);
    if (!novel) {
      setImportError('Could not read this file. Make sure it\'s a valid Write Library project.');
      setTimeout(() => setImportError(null), 4000);
      return;
    }

    importNovel(novel);
    setImportSuccess(`"${novel.title}" imported successfully!`);
    setTimeout(() => {
      setImportSuccess(null);
      onSelectNovel(); // navigate into the manuscript
    }, 1200);
  }, [importNovel, onSelectNovel]);

  // Button-based import
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await handleImportFile(file);
    // Reset so same file can be re-imported
    e.target.value = '';
  };

  // Drag-and-drop handlers on the whole dashboard
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDraggingOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDraggingOver(false);
    const file = e.dataTransfer.files[0];
    if (file) await handleImportFile(file);
  };

  // Warm Start context — find the most recently touched chapter
  const lastNovel = novels.find(n => n.id === activeNovelId) || novels[0];
  const lastChapter = lastNovel?.chapters
    .slice()
    .sort((a, b) => (b.wordCount || 0) - (a.wordCount || 0))[0];

  return (
    <div
      className="h-full overflow-auto relative"
      style={{ backgroundColor: themeProfile.background }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >

      {/* ── Warm Start Overlay ── */}
      {showWarmStart && lastNovel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 animate-in fade-in duration-500"
          style={{ backgroundColor: `${themeProfile.background}E0`, backdropFilter: 'blur(12px)' }}
          onClick={() => setShowWarmStart(false)}
        >
          <div
            className="max-w-sm w-full rounded-3xl p-8 shadow-2xl animate-in slide-in-from-bottom-4 duration-500"
            style={{ backgroundColor: themeProfile.paperSurface, border: `1px solid ${themeProfile.border}` }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 mx-auto"
              style={{ backgroundColor: `${themeProfile.accent}20` }}
            >
              <PenLine className="w-7 h-7" style={{ color: themeProfile.accent }} />
            </div>

            {/* Greeting */}
            <h2
              className="text-2xl font-literary font-bold text-center mb-1"
              style={{ color: themeProfile.text }}
            >
              Welcome back{user?.pseudonym ? `, ${user.pseudonym}` : ''}.
            </h2>
            <p
              className="text-sm text-center mb-6"
              style={{ color: themeProfile.text, opacity: 0.5 }}
            >
              Ready to pick up where you left off?
            </p>

            {/* Last context card */}
            <div
              className="rounded-2xl p-4 mb-6"
              style={{ backgroundColor: themeProfile.background, border: `1px solid ${themeProfile.border}` }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center mt-0.5"
                  style={{ backgroundColor: `${themeProfile.accent}15` }}
                >
                  <BookOpen className="w-5 h-5" style={{ color: themeProfile.accent }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium mb-0.5" style={{ color: themeProfile.text, opacity: 0.5 }}>
                    {lastNovel.title}
                  </p>
                  <p className="font-semibold text-sm leading-snug line-clamp-2" style={{ color: themeProfile.text }}>
                    {lastChapter?.title || 'Chapter 1'}
                  </p>
                  {lastChapter && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <Clock className="w-3 h-3" style={{ color: themeProfile.text, opacity: 0.4 }} />
                      <span className="text-xs" style={{ color: themeProfile.text, opacity: 0.4 }}>
                        {(lastChapter.wordCount || 0).toLocaleString()} words written
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <button
              onClick={() => {
                setShowWarmStart(false);
                if (lastNovel) {
                  handleSelectNovel(lastNovel.id);
                }
              }}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm mb-3 transition-all hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]"
              style={{ backgroundColor: themeProfile.accent, color: themeProfile.background }}
            >
              Continue Writing
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowWarmStart(false)}
              className="w-full py-2.5 rounded-xl text-sm font-medium hover:opacity-70 transition-opacity"
              style={{ color: themeProfile.text, opacity: 0.5 }}
            >
              Browse Library
            </button>
          </div>
        </div>
      )}
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".nexus,.json"
        className="hidden"
        onChange={handleFileInputChange}
      />

      {/* Drag-over overlay */}
      {isDraggingOver && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center pointer-events-none"
          style={{
            backgroundColor: `${themeProfile.accent}22`,
            border: `3px dashed ${themeProfile.accent}`,
            backdropFilter: 'blur(4px)',
          }}
        >
          <Upload className="w-16 h-16 mb-4" style={{ color: themeProfile.accent }} />
          <p className="text-2xl font-literary font-semibold" style={{ color: themeProfile.accent }}>
            Drop to Import Project
          </p>
          <p className="text-sm mt-2" style={{ color: themeProfile.text, opacity: 0.7 }}>
            Release your .nexus file to open it in Write Library
          </p>
        </div>
      )}

      {/* Toast notifications */}
      {(importError || importSuccess) && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl text-sm font-medium shadow-lg flex items-center gap-2 animate-gentle-fade-in"
          style={{
            backgroundColor: importError ? '#ef4444' : themeProfile.accent,
            color: 'white',
          }}
        >
          {importError ?? importSuccess}
        </div>
      )}

      {/* Header */}
      <header
        className="sticky top-0 z-10 px-8 py-6"
        style={{
          backgroundColor: themeProfile.background,
          borderBottom: `1px solid ${themeProfile.border}`,
        }}
      >
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
              style={{ backgroundColor: themeProfile.accent }}
            >
              <BookOpen className="w-5 h-5" style={{ color: themeProfile.background }} />
            </div>
            <div>
              <h1
                className="text-2xl font-literary font-semibold"
                style={{ color: themeProfile.text }}
              >
                The Studio
              </h1>
              <p className="text-sm" style={{ color: themeProfile.text, opacity: 0.5 }}>
                {novels.length} {novels.length === 1 ? 'project' : 'projects'} · drop a .nexus file anywhere to import
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Admin Toggle - Lonnie Only */}
            {(user?.email === 'lonnie@writelibrary.com' || user?.pseudonym === 'lonnie') && (
              <button
                onClick={() => setViewMode('admin')}
                className="flex items-center gap-2 px-3 py-2.5 rounded-xl font-bold transition-all hover:scale-105 text-sm border border-dashed"
                style={{
                  backgroundColor: '#d9770615',
                  borderColor: '#d9770640',
                  color: '#d97706',
                }}
                title="Admin Back-end"
              >
                <ShieldCheck className="w-4 h-4" />
                {!isMobile && <span>Admin CRM</span>}
              </button>
            )}

            {/* Inbox Button */}
            <button
              onClick={() => setShowInbox(true)}
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl font-medium transition-all hover:scale-105 text-sm relative"
              style={{
                backgroundColor: themeProfile.paperSurface,
                border: `1px solid ${themeProfile.border}`,
                color: themeProfile.accent,
              }}
              title="Reader Inbox — Cappuccino tips"
            >
              <Inbox className="w-4 h-4" />
              {!isMobile && <span>Inbox</span>}
              {inboxTips.length > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center text-white"
                  style={{ backgroundColor: themeProfile.accent }}
                >
                  {inboxTips.length > 9 ? '9+' : inboxTips.length}
                </span>
              )}
            </button>

            {/* Import Button */}
            <button
              onClick={handleImportClick}
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl font-medium transition-all hover:scale-105 text-sm"
              style={{
                backgroundColor: themeProfile.paperSurface,
                border: `1px solid ${themeProfile.border}`,
                color: themeProfile.text,
              }}
              title="Import a .nexus project file"
            >
              <FolderOpen className="w-4 h-4" />
              {!isMobile && <span>Import</span>}
            </button>

            {/* Enter Coffee Shop Button */}
            <Link
              to="/read"
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl font-medium transition-all hover:scale-105 text-sm"
              style={{
                backgroundColor: themeProfile.paperSurface,
                border: `1px solid ${themeProfile.border}`,
                color: themeProfile.accent,
              }}
            >
              <Coffee className="w-4 h-4" />
              {!isMobile && <span>Enter Coffee Shop</span>}
            </Link>

            {/* Profile/Log Out Group */}
            <div className="flex items-center gap-2 ml-2 pl-4 border-l" style={{ borderColor: themeProfile.border }}>
              {user && (
                <div className="flex items-center gap-2 px-2 py-1 rounded-lg" style={{ color: themeProfile.text }}>
                  <UserIcon className="w-4 h-4 opacity-70" />
                  {!isMobile && <span className="text-sm font-medium">{user.pseudonym}</span>}
                </div>
              )}

              <button
                onClick={() => logout()}
                className="p-2.5 rounded-xl transition-all hover:scale-105 hover:bg-red-500/10 text-red-500"
                title="Log Out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>

            {/* Settings Button */}
            <button
              onClick={() => setShowSettings(true)}
              className="p-2.5 rounded-xl transition-all hover:scale-105"
              style={{
                backgroundColor: themeProfile.paperSurface,
                border: `1px solid ${themeProfile.border}`,
              }}
              title="Settings"
            >
              <Settings className="w-5 h-5" style={{ color: themeProfile.text, opacity: 0.6 }} />
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-xl transition-all hover:scale-105"
              style={{
                backgroundColor: themeProfile.paperSurface,
                border: `1px solid ${themeProfile.border}`,
              }}
              title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5" style={{ color: themeProfile.text, opacity: 0.6 }} />
              ) : (
                <Sun className="w-5 h-5" style={{ color: themeProfile.text, opacity: 0.6 }} />
              )}
            </button>

            {/* Create Button */}
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all hover:scale-105 hover:opacity-90"
              style={{ backgroundColor: themeProfile.accent, color: themeProfile.background }}
            >
              <Plus className="w-4 h-4" />
              <span>New Novel</span>
            </button>
          </div>
        </div>
      </header>

      {/* Bookshelf */}
      <main className={cn('max-w-6xl mx-auto py-8 md:py-12', isMobile ? 'px-4' : 'px-8')}>
        {/* Daily Prompt Card */}
        <div className="mb-8">
          <DailyPromptCard />
        </div>
        <div className="relative">
          {/* Wood Shelf */}
          <div
            className="absolute inset-x-0 bottom-0 h-3 md:h-4 rounded-sm"
            style={{
              background: `linear-gradient(to bottom, ${themeProfile.border}, ${themeProfile.border}88)`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}
          />

          {/* Books Grid */}
          <div className={cn('flex items-end gap-1 pb-3 md:pb-4', isMobile ? 'overflow-x-auto px-2 min-h-[200px]' : 'px-4 min-h-[320px]')}>
            {novels.map((novel, index) => {
              const spineStyle = bookSpineColors[index % bookSpineColors.length];
              const isActive = novel.id === activeNovelId;
              const wordCount = novel.chapters.reduce((sum, ch) => sum + (ch.wordCount || 0), 0);
              const spineWidth = getSpineWidth(wordCount, isMobile);

              return (
                <div
                  key={novel.id}
                  className="group relative flex-shrink-0"
                  style={{
                    height: isMobile ? `${160 + (index % 2) * 30}px` : `${240 + (index % 3) * 40}px`,
                    width: `${spineWidth}px`,
                  }}
                >
                  {/* Book Spine */}
                  <button
                    onClick={() => handleSelectNovel(novel.id)}
                    className={cn(
                      'w-full h-full rounded-t-sm relative overflow-hidden transition-all duration-300',
                      'active:scale-95 md:hover:-translate-y-3 md:hover:shadow-2xl',
                    )}
                    style={{
                      backgroundColor: spineStyle.bg,
                      backgroundImage: generateBookPattern(index, spineStyle.texture as 'cloth' | 'leather'),
                      boxShadow: isActive
                        ? `0 12px 32px rgba(0,0,0,0.4), 0 0 0 2px ${themeProfile.accent}, inset 0 0 0 1px rgba(255,255,255,0.1)`
                        : '0 6px 16px rgba(0,0,0,0.3), inset 0 0 0 1px rgba(255,255,255,0.05)',
                      transform: isActive ? 'translateY(-6px) rotateY(-3deg)' : undefined,
                    }}
                  >
                    {/* 3D spine highlight */}
                    <div className="absolute inset-y-0 left-0 w-1.5" style={{ background: 'linear-gradient(to right, rgba(255,255,255,0.25), rgba(255,255,255,0.08), transparent)' }} />
                    
                    {/* Subtle wear effect on older books (based on index) */}
                    {index % 4 === 0 && (
                      <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(ellipse at 20% 50%, rgba(0,0,0,0.3) 0%, transparent 50%)' }} />
                    )}
                    
                    <div className="absolute inset-0 flex items-center justify-center p-1.5 md:p-2">
                      <span
                        className="font-literary text-[10px] md:text-xs font-semibold whitespace-nowrap"
                        style={{
                          color: spineStyle.text,
                          writingMode: 'vertical-rl',
                          textOrientation: 'mixed',
                          transform: 'rotate(180deg)',
                          textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                          maxHeight: '75%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          letterSpacing: '0.02em',
                        }}
                        title={novel.title}
                      >
                        {novel.title}
                      </span>
                    </div>
                    {/* Top band */}
                    <div 
                      className="absolute top-3 md:top-4 left-1 right-1 h-2 md:h-2.5 rounded-full" 
                      style={{ 
                        backgroundColor: spineStyle.accent, 
                        opacity: 0.7,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                      }} 
                    />
                    {/* Bottom band */}
                    <div 
                      className="absolute bottom-6 md:bottom-8 left-1 right-1 h-1.5 md:h-2 rounded-full" 
                      style={{ 
                        backgroundColor: spineStyle.accent, 
                        opacity: 0.5,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                      }} 
                    />
                    {/* Word count badge */}
                    <div
                      className="absolute bottom-1.5 md:bottom-2 left-1/2 -translate-x-1/2 text-[7px] md:text-[8px] px-1.5 py-0.5 rounded-full font-medium"
                      style={{ 
                        background: 'linear-gradient(135deg, rgba(0,0,0,0.5), rgba(0,0,0,0.3))',
                        color: spineStyle.text,
                        border: '1px solid rgba(255,255,255,0.1)',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.3)'
                      }}
                    >
                      {wordCount >= 1000 ? `${(wordCount / 1000).toFixed(1)}k` : wordCount}
                    </div>
                  </button>

                  {/* Menu Button */}
                  {!isMobile && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === novel.id ? null : novel.id); }}
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ backgroundColor: themeProfile.paperSurface, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                    >
                      <MoreVertical className="w-3 h-3" style={{ color: themeProfile.text }} />
                    </button>
                  )}

                  {/* Dropdown */}
                  {menuOpen === novel.id && (
                    <div
                      className="absolute top-4 right-0 z-20 py-1 rounded-lg min-w-[140px]"
                      style={{ backgroundColor: themeProfile.paperSurface, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', border: `1px solid ${themeProfile.border}` }}
                    >
                      <button
                        onClick={() => handleExportDocx(novel.id)}
                        className="w-full px-3 py-2 text-left text-xs font-medium hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                        style={{ color: themeProfile.text }}
                      >
                        Export .docx
                      </button>
                      <button
                        onClick={() => handleDownloadBackup(novel.id)}
                        className="w-full px-3 py-2 text-left text-xs font-medium hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                        style={{ color: themeProfile.text }}
                      >
                        Download Backup
                      </button>
                      <div className="my-1 border-t" style={{ borderColor: themeProfile.border }} />
                      <button
                        onClick={() => handleDeleteNovel(novel.id)}
                        className="w-full px-3 py-2 text-left text-xs font-medium text-red-500 hover:bg-red-50 transition-colors"
                      >
                        Delete Project
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Add New Book Slot */}
            <button
              onClick={() => setShowCreateModal(true)}
              className="group flex flex-col items-center justify-center rounded-t-sm transition-all duration-300 flex-shrink-0 active:scale-95 md:hover:-translate-y-3 md:hover:shadow-2xl"
              style={{
                height: isMobile ? '180px' : '280px',
                width: '52px',
                background: `linear-gradient(135deg, ${themeProfile.accent}15, ${themeProfile.accent}08)`,
                border: `2px dashed ${themeProfile.accent}50`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(255,255,255,0.05)',
              }}
            >
              <div 
                className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center mb-2 transition-all group-hover:scale-110 group-active:scale-95"
                style={{ backgroundColor: `${themeProfile.accent}20` }}
              >
                <Plus className="w-5 h-5 md:w-6 md:h-6" style={{ color: themeProfile.accent }} />
              </div>
              <span className="text-[9px] md:text-[10px] font-medium tracking-wide" style={{ color: themeProfile.accent, opacity: 0.7 }}>
                New Novel
              </span>
            </button>
          </div>
        </div>

        {/* Empty State */}
        {novels.length === 0 && (
          <div className="text-center py-16 md:py-24">
            <Sparkles className="w-10 h-10 md:w-14 md:h-14 mx-auto mb-6 opacity-30" style={{ color: themeProfile.accent }} />
            <p className="font-literary text-xl md:text-2xl mb-2 font-semibold" style={{ color: themeProfile.text }}>
              Your library is empty
            </p>
            <p className="text-sm mb-8" style={{ color: themeProfile.text, opacity: 0.5 }}>
              Start a new novel, or import an existing project
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-sm transition-all hover:opacity-90 hover:scale-105"
                style={{ backgroundColor: themeProfile.accent, color: themeProfile.background }}
              >
                <Plus className="w-4 h-4" />
                New Novel
              </button>
              <button
                onClick={handleImportClick}
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-sm transition-all hover:scale-105"
                style={{ backgroundColor: themeProfile.paperSurface, border: `1px solid ${themeProfile.border}`, color: themeProfile.text }}
              >
                <Upload className="w-4 h-4" />
                Import .nexus File
              </button>
            </div>
            <p className="text-xs mt-6" style={{ color: themeProfile.text, opacity: 0.35 }}>
              You can also drag and drop a .nexus file anywhere on this page
            </p>
          </div>
        )}
        {/* Trust Badge / Privacy Footer */}
        <div className="mt-auto pt-16 pb-8 text-center flex flex-col items-center justify-center opacity-40 hover:opacity-80 transition-opacity cursor-default">
          <div className="flex items-center gap-2 text-xs font-medium" style={{ color: themeProfile.text }}>
            <span className="w-1.5 h-1.5 rounded-full bg-green-500/80 shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
            Your data is synced securely to the cloud.
          </div>
          <p className="text-[10px] mt-1" style={{ color: themeProfile.text, opacity: 0.6 }}>
            Write Library keeps your drafts encrypted and securely backed up.
          </p>
        </div>
      </main>

      {/* ── Author Inbox Drawer ── */}
      {showInbox && (
        <div
          className="fixed inset-0 z-50 flex justify-end"
          onClick={() => setShowInbox(false)}
        >
          <div
            className="h-full w-full max-w-md shadow-2xl flex flex-col animate-in slide-in-from-right duration-300"
            style={{ backgroundColor: themeProfile.paperSurface }}
            onClick={e => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div
              className="flex items-center justify-between px-6 py-5 border-b"
              style={{ borderColor: themeProfile.border }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${themeProfile.accent}15` }}
                >
                  <Inbox className="w-5 h-5" style={{ color: themeProfile.accent }} />
                </div>
                <div>
                  <h2 className="font-bold text-base" style={{ color: themeProfile.text }}>Reader Inbox</h2>
                  <p className="text-xs" style={{ color: themeProfile.text, opacity: 0.45 }}>Cappuccino tips from your readers</p>
                </div>
              </div>
              <button
                onClick={() => setShowInbox(false)}
                className="p-2 rounded-lg hover:opacity-60 transition-opacity"
                style={{ color: themeProfile.text }}
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Drawer Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {inboxLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: `${themeProfile.accent}40`, borderTopColor: themeProfile.accent }} />
                </div>
              ) : inboxTips.length === 0 ? (
                <div className="text-center py-16">
                  <MessageSquareQuote className="w-10 h-10 mx-auto mb-4 opacity-20" style={{ color: themeProfile.text }} />
                  <p className="font-literary text-base" style={{ color: themeProfile.text, opacity: 0.5 }}>No tips yet</p>
                  <p className="text-sm mt-1" style={{ color: themeProfile.text, opacity: 0.35 }}>When readers leave Cappuccino tips on your work, they'll appear here.</p>
                </div>
              ) : (
                inboxTips.map(tip => (
                  <div
                    key={tip.id}
                    className="rounded-2xl p-4 space-y-3"
                    style={{ backgroundColor: themeProfile.background, border: `1px solid ${themeProfile.border}` }}
                  >
                    {/* Highlighted text */}
                    <blockquote
                      className="text-sm italic border-l-[3px] pl-3 leading-relaxed"
                      style={{
                        borderColor: themeProfile.accent,
                        color: themeProfile.text,
                        opacity: 0.7,
                      }}
                    >
                      "{tip.highlightedText}"
                    </blockquote>

                    {/* Reader note */}
                    {tip.note && (
                      <p className="text-sm" style={{ color: themeProfile.text }}>
                        {tip.note}
                      </p>
                    )}

                    {/* Meta */}
                    <div className="flex items-center justify-between text-xs" style={{ color: themeProfile.text, opacity: 0.4 }}>
                      <span>☕ Cappuccino tip</span>
                      <span>{new Date(tip.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      {showCreateModal && (
        <CreateNovelForm onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  );
}
